import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { store } from "../../app/store";
import HeaderMenu from "../../layout/HeaderMenu";
import { Footer } from "../../screens/Footer/Footer";
import { AssistanceBoxComponent } from "../../ui/components/AssistanceBoxComponent";
import OrderDetailCard from "../../ui/components/MyOrders/OrderDetailCard";
import { OrderSummary } from "../../ui/components/MyOrders/OrderSummary";
import { TopBar } from "../../ui/components/TopBar/TopBar";
import { TwentyIcArrowLineLeft } from "../../ui/components/icons/TwentyIcArrowLineLeft";

import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import {
  MeetingCreateRequest,
  createMeetingAsync,
} from "../Meetings/MeetingSlice";
import { getFilteredUsersInfoPaginationAsync } from "../MyTeam/TeamUsersInfoSlice";
import { WellnessBody } from "../Wellness/WellnessSlice/WellnessBodySlice";
import {
  MyOrderDetail,
  getOrderAsync,
  myOrderDetailSelector,
  getBookingByIdAsync,
  selectCurrentBooking,
  updateBookingPrenotazioneAsync,
} from "./MyOrderDetailSlice";
import "./OrderDetail.css";

export interface WellnessBodyOrderDetailProps {}

// Interfaccia corretta per i dati della prenotazione Calendly
interface CalendlyEventData {
  event: string; // Tipo di evento (es: "calendly.event_scheduled")
  payload: {
    event: {
      uri: string;
      start_time?: string;
      end_time?: string;
      name?: string;
      status?: string;
      created_at?: string;
      updated_at?: string;
      location?: {
        type: string;
        location?: string;
      };
      invitees_counter?: {
        total: number;
        active: number;
        limit: number;
      };
      event_memberships?: Array<{
        user: string;
        user_name: string;
        user_email: string;
      }>;
    };
    invitee?: {
      uri: string;
      name?: string;
      email?: string;
      text_reminder_number?: string;
      timezone?: string;
      created_at?: string;
      updated_at?: string;
      canceled?: boolean;
      cancellation?: {
        canceled_by: string;
        reason: string;
      };
      payment?: {
        external_id: string;
        provider: string;
        amount: number;
        currency: string;
        terms: string;
        successful: boolean;
      };
      questions_and_answers?: Array<{
        question: string;
        answer: string;
        position: number;
      }>;
      tracking?: {
        utm_campaign?: string;
        utm_source?: string;
        utm_medium?: string;
        utm_content?: string;
        utm_term?: string;
        salesforce_uuid?: string;
      };
    };
  };
}

const WellnessBodyOrderDetail: React.FC<WellnessBodyOrderDetailProps> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [id, setId] = useState<number>(-1);
  const [wellnessBody, setWellnessBody] = useState<WellnessBody>();
  const [bookingData, setBookingData] = useState<CalendlyEventData | null>(
    null
  );

  const firstName = localStorage.getItem("firstName");
  const lastName = localStorage.getItem("lastName");
  const businessName = localStorage.getItem("businessName");

  const orderRoute = useMatch("wellness/body/myOrders/:id");

  // Selettori Redux
  let order: MyOrderDetail = useAppSelector(
    (state) =>
      myOrderDetailSelector.selectById(state, id) ?? ({} as MyOrderDetail)
  );

  // ✨ CAMBIATO: Usa il booking corrente invece di bookingExistsResult
  const currentBooking = useAppSelector(selectCurrentBooking);

  // Listener per quando viene schedulato un evento
  useCalendlyEventListener({
    onEventScheduled: (e) => {
      console.log("🎯 Evento schedulato:", e.data);
      handleEventScheduled(e.data);
    },
  });

  // Listener per quando viene mostrato il popup di data/ora
  useCalendlyEventListener({
    onDateAndTimeSelected: (e) => {
      console.log("📅 Data e ora selezionate:", e.data);
      handleDateAndTimeSelected(e.data);
    },
  });

  // Listener per quando viene mostrato il popup per inserire i dettagli
  useCalendlyEventListener({
    onEventTypeViewed: (e) => {
      console.log("👀 Tipo di evento visualizzato:", e.data);
      handleEventTypeViewed(e.data);
    },
  });

  // Listener per quando il profilo viene visualizzato
  useCalendlyEventListener({
    onProfilePageViewed: (e) => {
      console.log("👤 Profilo visualizzato:", e.data);
      handleProfilePageViewed(e.data);
    },
  });

  // Gestisce i dati quando viene schedulato l'evento
  function handleEventScheduled(data: CalendlyEventData) {
    console.log("📝 Creazione meeting da evento schedulato...");

    // Salva tutti i dati della prenotazione
    setBookingData(data);

    // Log dettagliato dei dati ricevuti
    console.group("📊 Dati completi della prenotazione:");
    console.log("Tipo evento:", data.event);
    console.log("URI evento:", data.payload?.event?.uri);
    console.log("Nome evento:", data.payload?.event?.name);
    console.log("Data inizio:", data.payload?.event?.start_time);
    console.log("Data fine:", data.payload?.event?.end_time);
    console.log("Status:", data.payload?.event?.status);
    console.log("Location:", data.payload?.event?.location);

    if (data.payload?.invitee) {
      console.log("Nome invitato:", data.payload.invitee.name);
      console.log("Email invitato:", data.payload.invitee.email);
      console.log("Telefono:", data.payload.invitee.text_reminder_number);
      console.log("Timezone:", data.payload.invitee.timezone);
      console.log(
        "Domande e risposte:",
        data.payload.invitee.questions_and_answers
      );
      console.log("Tracking:", data.payload.invitee.tracking);
      console.log("Pagamento:", data.payload.invitee.payment);
    }
    console.groupEnd();

    // Crea il meeting come già fai
    createMeetingFromEvent(data);

    // ✨ AGGIORNA IL CAMPO PRENOTAZIONE A TRUE
    dispatch(
      updateBookingPrenotazioneAsync({
        bookingId: order.id,
        prenotazione: true,
      })
    );

    console.log(
      `🔄 Aggiornamento campo prenotazione per booking ID ${order.id} a TRUE`
    );

    // Qui puoi aggiungere ulteriori azioni con i dati catturati
    handleBookingData(data);
  }

  // Gestisce i dati quando viene selezionata data e ora
  function handleDateAndTimeSelected(data: any) {
    console.log("📅 Dati selezione data/ora:", {
      eventType: data.event_type_name,
      startTime: data.start_time,
      endTime: data.end_time,
    });
  }

  // Gestisce quando viene visualizzato il tipo di evento
  function handleEventTypeViewed(data: any) {
    console.log("👀 Tipo evento visualizzato:", {
      eventType: data.event_type_name,
      eventTypeUuid: data.event_type_uuid,
    });
  }

  // Gestisce quando viene visualizzato il profilo
  function handleProfilePageViewed(data: any) {
    console.log("👤 Profilo visualizzato:", data);
  }

  // Elabora i dati della prenotazione per utilizzi futuri
  function handleBookingData(data: CalendlyEventData) {
    console.log("🔄 Elaborazione dati prenotazione...");

    // Estrai informazioni utili dal payload
    const bookingInfo = {
      eventType: data.event,
      eventUri: data.payload?.event?.uri,
      eventName: data.payload?.event?.name,
      startTime: data.payload?.event?.start_time,
      endTime: data.payload?.event?.end_time,
      status: data.payload?.event?.status,
      location: data.payload?.event?.location,
      attendeeName: data.payload?.invitee?.name,
      attendeeEmail: data.payload?.invitee?.email,
      attendeePhone: data.payload?.invitee?.text_reminder_number,
      timezone: data.payload?.invitee?.timezone,
      questionsAndAnswers: data.payload?.invitee?.questions_and_answers,
      trackingData: data.payload?.invitee?.tracking,
      paymentInfo: data.payload?.invitee?.payment,
      createdAt: data.payload?.event?.created_at,
      updatedAt: data.payload?.event?.updated_at,
    };

    // Salva i dati nel localStorage per persistenza (opzionale)
    localStorage.setItem(`booking_${order.id}`, JSON.stringify(bookingInfo));

    console.log("✅ Dati prenotazione elaborati e salvati:", bookingInfo);
  }

  function createMeetingFromEvent(data: CalendlyEventData) {
    const currentDate = new Date();
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "Europe/Rome",
      hour12: false,
    };
    const italyTimeString = currentDate.toLocaleString("en-US", options);

    // Usa solo i campi esistenti nella tua interfaccia MeetingCreateRequest
    const meetingFromPayload: MeetingCreateRequest = {
      invoiceDate: new Date(italyTimeString),
      invoicePrice: order.wellnessBody?.price ?? 0,
      meetingInvoiceStatus: "NOT_INVOICED",
      meetingStatus: "NOT_APPROVED",
      bookingId: order.id ?? -1,
      providerId: order.wellnessBody?.userInfo.id ?? -1,
      tenantId: order.wellnessBody?.tenant.id ?? -1,
    };

    store.dispatch(createMeetingAsync({ meeting: meetingFromPayload }));

    // Salva i dati Calendly separatamente se necessario
    const calendlyData = {
      eventUri: data.payload?.event?.uri,
      scheduledStartTime: data.payload?.event?.start_time,
      scheduledEndTime: data.payload?.event?.end_time,
      attendeeName: data.payload?.invitee?.name,
      attendeeEmail: data.payload?.invitee?.email,
    };

    // Potresti voler inviare questi dati separatamente o salvarli
    console.log("📊 Dati Calendly da gestire separatamente:", calendlyData);
  }

  useEffect(() => {
    if (orderRoute !== null) {
      if (orderRoute.params.id) {
        const bookingId = parseInt(orderRoute.params.id);
        setId(bookingId);
        store.dispatch(getOrderAsync(bookingId));
        store.dispatch(getFilteredUsersInfoPaginationAsync());

        // ✨ CAMBIATO: Usa la GET semplice per il booking
        dispatch(getBookingByIdAsync(bookingId));

        return;
      }
    }
  }, [orderRoute, dispatch]);

  useEffect(() => {
    if (order.wellnessBody) {
      setWellnessBody(order.wellnessBody);
    }
  }, [order]);

  // ✨ NUOVO: Log per il booking corrente
  useEffect(() => {
    if (currentBooking) {
      console.log("🔍 BOOKING CORRENTE:");
      console.log("- Booking ID:", currentBooking.id);
      console.log("- Prenotazione:", currentBooking.prenotazione);
      console.log("- Oggetto completo:", currentBooking);

      if (currentBooking.prenotazione) {
        console.log("✅ L'APPUNTAMENTO È GIÀ PRENOTATO");
      } else {
        console.log("📅 L'APPUNTAMENTO NON È ANCORA PRENOTATO");
      }
    } else {
      console.log("⏳ Caricamento booking in corso...");
    }
  }, [currentBooking]);

  // Utilità per visualizzare i dati della prenotazione (per debug)
  const renderBookingInfo = () => {
    if (!bookingData) return null;

    return (
      <div
        className="booking-info-debug"
        style={{
          background: "#f5f5f5",
          padding: "10px",
          margin: "10px 0",
          borderRadius: "5px",
          fontSize: "12px",
        }}
      >
        <h4>📋 Dati Prenotazione (Debug)</h4>
        <p>
          <strong>Tipo evento:</strong> {bookingData.event}
        </p>
        <p>
          <strong>Evento:</strong> {bookingData.payload?.event?.name}
        </p>
        <p>
          <strong>Data:</strong> {bookingData.payload?.event?.start_time}
        </p>
        <p>
          <strong>Partecipante:</strong> {bookingData.payload?.invitee?.name} (
          {bookingData.payload?.invitee?.email})
        </p>
        <p>
          <strong>Status:</strong> {bookingData.payload?.event?.status}
        </p>
      </div>
    );
  };

  // ✨ FUNZIONE AGGIORNATA: Usa currentBooking invece di bookingExistsResult
  const renderBookingContent = () => {
    // Se stiamo ancora caricando, mostra un loader
    if (!currentBooking) {
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>Caricamento booking...</p>
        </div>
      );
    }

    // ✨ CONTROLLO SEMPLICE: Se prenotazione è true, mostra messaggio
    if (currentBooking.prenotazione) {
      return (
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            padding: "40px 30px",
            textAlign: "center",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e5e5e5",
            margin: "20px auto",
            maxWidth: "700px",
          }}
        >
          {/* Checkmark Icon */}
          <div
            style={{
              width: "90px",
              height: "90px",
              backgroundColor: "#22c55e",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px auto",
            }}
          >
            <svg
              width="45"
              height="45"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20,6 9,17 4,12"></polyline>
            </svg>
          </div>

          <h2
            style={{
              fontSize: "28px",
              fontWeight: "600",
              color: "#1a1a1a",
              margin: "0 0 16px 0",
              lineHeight: "1.3",
            }}
          >
            Appuntamento Prenotato
          </h2>

          <p
            style={{
              fontSize: "16px",
              color: "#666666",
              margin: "0",
              lineHeight: "1.5",
            }}
          >
            I dettagli del tuo appuntamento sono stati inviati alla tua email.
          </p>
        </div>
      );
    }

    // Se prenotazione è false, mostra il widget Calendly
    if (wellnessBody?.link) {
      return (
        <div className="calendly-inline-widget">
          <InlineWidget url={wellnessBody.link} />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {wellnessBody && (
        <>
          <HeaderMenu>
            <TopBar
              title={wellnessBody.name}
              hasEdit={false}
              hasDate={false}
              breadcrumbText={
                "Servizi per il benessere > Benessere psico-fisico > Dettaglio ordine"
              }
              icon={<TwentyIcArrowLineLeft className="icon-instance-node-3" />}
              onClick={() => navigate("/Wellness/Body/myOrders")}
            />
          </HeaderMenu>
          <div className="order-detail">
            <div className="order-detail-left-side">
              <OrderDetailCard
                id={order.id}
                date={order.bookingDate}
                price={wellnessBody.price}
                image={wellnessBody.image}
                status={order.status}
                personsNumber={order.numberOfPeople}
                isWellnessService={true}
              />
              {/* Visualizza i dati della prenotazione per debug */}
              {process.env.NODE_ENV === "development" && renderBookingInfo()}

              {/* ✨ CONTENUTO CONDIZIONALE BASATO SUL CAMPO PRENOTAZIONE */}
              {renderBookingContent()}
            </div>
            <div className="order-detail-right-side">
              <OrderSummary
                price={wellnessBody.price}
                numberOfPeople={order.numberOfPeople}
                bookingStatus={order.status}
                isWellnessService={true}
              />
              <div className="summary-column">
                <AssistanceBoxComponent
                  assistance={t(
                    "smartPlanDetail.componentAssistantBox.assistance"
                  )}
                  whatsApp={t("smartPlanDetail.componentAssistantBox.whatsApp")}
                  date={t("smartPlanDetail.componentAssistantBox.date")}
                  whatsAppNumber={3773604135}
                  whatsAppMessageText={"Ciao, sono firstName* lastName* dell'azienda companyName*, ho bisogno di assistenza."
                    .replace("firstName*", firstName ?? "")
                    .replace("lastName*", lastName ?? "")
                    .replace("companyName*", businessName ?? "")}
                  disabled={undefined}
                />
              </div>
            </div>
          </div>
          <Footer />
        </>
      )}
    </>
  );
};

export default WellnessBodyOrderDetail;
